import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { AlertTriangle, ArrowLeft, RefreshCw, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, reports } from '@/api/client';
import { qk, useMe, usePaymentDetail } from '@/api/queries';
import { Empty, ErrorBox } from '@/components/empty';
import { Json } from '@/components/json-view';
import { PaymentStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PaymentStatus } from '@/api/types';
import { formatLyd } from '@/lib/utils';

const TERMINAL: ReadonlySet<PaymentStatus> = new Set([
  'succeeded',
  'failed',
  'expired',
  'refunded',
]);

export const Route = createFileRoute('/_authed/payments/$paymentId')({
  component: PaymentDetail,
});

function PaymentDetail() {
  const { t } = useTranslation();
  const { paymentId } = Route.useParams();
  const { data, isLoading, isError } = usePaymentDetail(paymentId);
  const me = useMe();

  if (isError) return <ErrorBox message={t('payments.detail.not_found')} />;
  if (isLoading || !data) return <Empty title={t('common.loading')} />;

  const isNonTerminal = !TERMINAL.has(data.status);
  const role = me.data?.effective_role;
  const canRepoll = role === 'admin' || role === 'operator';
  const canForceResolve = role === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/payments">
            <ArrowLeft className="h-4 w-4" /> {t('payments.detail.back_to_list')}
          </Link>
        </Button>
      </div>

      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.merchant_order_ref}</h1>
          <p className="text-sm text-muted-foreground font-mono">{data.id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PaymentStatusBadge status={data.status} />
          {isNonTerminal && canRepoll && <RepollButton paymentId={data.id} />}
          {isNonTerminal && canForceResolve && (
            <ForceResolveButton paymentId={data.id} currentStatus={data.status} />
          )}
          {data.status === 'succeeded' && (
            <RecordRefundButton paymentId={data.id} maxAmountMinor={data.amount_minor} />
          )}
        </div>
      </header>

      <section className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Field label={t('payments.cols.psp')} value={data.psp_code} />
        <Field label={t('payments.cols.amount')} value={formatLyd(data.amount_minor)} />
        <Field label={t('payments.cols.psp_ref')} value={data.psp_ref ?? '—'} mono />
        <Field label={t('payments.cols.currency')} value={data.currency} />
      </section>

      <Tabs defaultValue="timeline">
        <TabsList>
          <TabsTrigger value="timeline">{t('payments.detail.timeline')}</TabsTrigger>
          <TabsTrigger value="psp">{t('payments.detail.psp_payload')}</TabsTrigger>
          <TabsTrigger value="metadata">{t('payments.detail.metadata')}</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('payments.detail.events')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="relative border-s border-border ps-5 space-y-4">
                {data.events.map((e) => (
                  <li key={e.id}>
                    <span className="absolute -start-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary" />
                    <div className="text-xs text-muted-foreground font-mono">
                      {dayjs(e.created_at).format('YYYY-MM-DD HH:mm:ss')} · {e.source}
                    </div>
                    <div className="text-sm font-medium">
                      {e.event_type}
                      {e.new_status && (
                        <span className="text-muted-foreground"> → {e.new_status}</span>
                      )}
                    </div>
                    {Object.keys(e.payload ?? {}).length > 0 && (
                      <div className="mt-2 rounded-md border border-border p-2 bg-muted/30">
                        <Json data={e.payload} />
                      </div>
                    )}
                  </li>
                ))}
              </ol>
              {data.events.length === 0 && <Empty title={t('payments.detail.no_events')} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="psp">
          <Card>
            <CardHeader>
              <CardTitle>{t('payments.detail.psp_payload')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Json data={data.psp_payload} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metadata">
          <Card>
            <CardHeader>
              <CardTitle>{t('payments.detail.metadata')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Json data={data.metadata} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? 'mt-1 text-sm font-mono' : 'mt-1 text-sm font-medium capitalize'}>
        {value}
      </div>
    </div>
  );
}

function RepollButton({ paymentId }: { paymentId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => reports.repollPayment(paymentId),
    onSuccess: (res) => {
      toast.success(
        res.transitioned
          ? (t('payments.detail.repoll_transitioned', { status: res.status }) as string)
          : (t('payments.detail.repoll_no_change') as string),
      );
      qc.invalidateQueries({ queryKey: qk.paymentDetail(paymentId) });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('payments.detail.repoll_failed') as string),
      ),
  });
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      <RefreshCw className={`h-4 w-4 ${mutation.isPending ? 'animate-spin' : ''}`} />
      {mutation.isPending
        ? t('payments.detail.repoll_busy')
        : t('payments.detail.repoll')}
    </Button>
  );
}

function ForceResolveButton({
  paymentId,
  currentStatus,
}: {
  paymentId: string;
  currentStatus: PaymentStatus;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<'succeeded' | 'failed' | 'expired'>('expired');
  const [reason, setReason] = useState('');

  // From `pending`, the state machine does not allow `succeeded` —
  // payments must go via `processing` first. Reflect that in the UI so
  // the button doesn't 422 the operator.
  const succeededDisabled = currentStatus === 'pending';

  const mutation = useMutation({
    mutationFn: () =>
      reports.forceResolvePayment(paymentId, { target_status: target, reason: reason.trim() }),
    onSuccess: () => {
      toast.success(t('payments.detail.force_resolve_success') as string);
      setOpen(false);
      setReason('');
      qc.invalidateQueries({ queryKey: qk.paymentDetail(paymentId) });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('payments.detail.force_resolve_failed') as string),
      ),
  });

  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        <AlertTriangle className="h-4 w-4" /> {t('payments.detail.force_resolve')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.detail.force_resolve_title')}</DialogTitle>
            <DialogDescription>{t('payments.detail.force_resolve_desc')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (reason.trim().length < 4) {
                toast.error(t('payments.detail.force_resolve_reason_required') as string);
                return;
              }
              mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="fr-target">
                {t('payments.detail.force_resolve_target_label')}
              </Label>
              <Select
                value={target}
                onValueChange={(v) => setTarget(v as 'succeeded' | 'failed' | 'expired')}
              >
                <SelectTrigger id="fr-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expired">
                    {t('payments.detail.force_resolve_target_expired')}
                  </SelectItem>
                  <SelectItem value="failed">
                    {t('payments.detail.force_resolve_target_failed')}
                  </SelectItem>
                  <SelectItem value="succeeded" disabled={succeededDisabled}>
                    {t('payments.detail.force_resolve_target_succeeded')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fr-reason">
                {t('payments.detail.force_resolve_reason_label')}
              </Label>
              <textarea
                id="fr-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={
                  t('payments.detail.force_resolve_reason_placeholder') as string
                }
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                minLength={4}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="destructive" disabled={mutation.isPending}>
                {t('payments.detail.force_resolve_confirm')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RecordRefundButton({
  paymentId,
  maxAmountMinor,
}: {
  paymentId: string;
  maxAmountMinor: number;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(maxAmountMinor));
  const [reason, setReason] = useState('');
  const [pspRef, setPspRef] = useState('');
  const [isFull, setIsFull] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      reports.recordRefund(paymentId, {
        amount_minor: Number(amount),
        reason: reason || undefined,
        psp_back_office_ref: pspRef || undefined,
        is_full: isFull,
      }),
    onSuccess: () => {
      toast.success(t('payments.detail.refund_success') as string);
      setOpen(false);
      qc.invalidateQueries({ queryKey: qk.paymentDetail(paymentId) });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : (t('payments.detail.refund_failed') as string)),
  });

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Undo2 className="h-4 w-4" /> {t('payments.detail.record_refund')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.detail.refund_dialog_title')}</DialogTitle>
            <DialogDescription>{t('payments.detail.refund_dialog_desc')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const n = Number(amount);
              if (!n || n < 1 || n > maxAmountMinor) {
                toast.error(t('common.required') as string);
                return;
              }
              mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="ref-amount">{t('payments.detail.refund_amount')}</Label>
              <Input
                id="ref-amount"
                type="number"
                min={1}
                max={maxAmountMinor}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ref-reason">{t('payments.detail.refund_reason')}</Label>
              <Input id="ref-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ref-psp">{t('payments.detail.refund_psp_ref')}</Label>
              <Input
                id="ref-psp"
                value={pspRef}
                onChange={(e) => setPspRef(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="ref-full"
                type="checkbox"
                checked={isFull}
                onChange={(e) => setIsFull(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="ref-full" className="text-sm font-normal">
                {isFull
                  ? t('payments.detail.refund_full')
                  : t('payments.detail.refund_partial')}
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
